import { createRoot, type Remix } from '@remix-run/dom';
import { events } from '@remix-run/events';
import { enter, backspace, escape } from '@remix-run/events/key';
import { press } from '@remix-run/events/press';
import { WordleGame, type GameStatus } from './wordle';
import {
  Board,
  Keyboard,
  Message,
  GameOverModal,
  Button,
} from './components';

function App(this: Remix.Handle<WordleGame>) {
  let game = new WordleGame();
  let message = '';
  let messageTimer: number;

  this.context.set(game);

  // Listen to game events
  events(game, [
    WordleGame.change(() => this.render()),
    WordleGame.gameOver(() => this.render()),
  ]);

  // Keyboard shortcuts
  events(document, [
    enter(() => {
      if (game.status === 'playing') {
        handleSubmit();
      }
    }),
    backspace(() => {
      if (game.status === 'playing') {
        game.removeLetter();
      }
    }),
    escape(() => {
      if (game.status !== 'playing') {
        game.reset();
      }
    }),
  ]);

  // Handle letter key presses
  events(document, [
    {
      type: 'keydown',
      handler: event => {
        if (game.status !== 'playing') return;

        const key = event.key.toUpperCase();
        if (/^[A-Z]$/.test(key)) {
          game.addLetter(key);
        }
      },
      options: undefined,
    },
  ]);

  const showMessage = (text: string) => {
    clearTimeout(messageTimer);
    message = text;
    this.render();
    messageTimer = window.setTimeout(() => {
      message = '';
      this.render();
    }, 2000);
  };

  const handleSubmit = () => {
    const result = game.submitGuess();
    if (!result.success && result.message) {
      showMessage(result.message);
    }
  };

  const handlePlayAgain = () => {
    game.reset();
  };

  return () => (
    <div
      css={{
        minHeight: '100vh',
        background: '#121213',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
      }}
    >
      <header
        css={{
          borderBottom: '1px solid #3a3a3c',
          width: '100%',
          maxWidth: '500px',
          marginBottom: '24px',
          paddingBottom: '16px',
        }}
      >
        <h1
          css={{
            fontSize: '36px',
            fontWeight: 'bold',
            textAlign: 'center',
            margin: '0',
            letterSpacing: '2px',
          }}
        >
          WORDLE
        </h1>
      </header>

      <main
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: '1',
          width: '100%',
          maxWidth: '500px',
        }}
      >
        <Board
          guesses={game.guesses}
          currentGuess={game.currentGuess}
          currentRow={game.currentRow}
          maxGuesses={game.maxGuesses}
        />

        <Keyboard
          onLetterPress={letter => game.addLetter(letter)}
          onEnter={handleSubmit}
          onBackspace={() => game.removeLetter()}
          getLetterStatus={letter => game.getLetterStatus(letter)}
        />

        <div
          css={{
            marginTop: '24px',
            display: 'flex',
            gap: '12px',
          }}
        >
          <Button
            on={[press(() => game.reset())]}
            css={{
              width: '150px',
              background: '#565758',
              '&:hover': {
                background: '#6a6b6c',
              },
            }}
          >
            NEW GAME
          </Button>
        </div>
      </main>

      {message && <Message message={message} />}

      {game.status !== 'playing' && (
        <GameOverModal
          status={game.status}
          answer={game.answer}
          guessCount={game.guesses.length}
          onPlayAgain={handlePlayAgain}
        />
      )}

      <style>
        {`
          @keyframes fadeInOut {
            0% {
              opacity: 0;
              transform: translateX(-50%) translateY(-10px);
            }
            10% {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
            90% {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateX(-50%) translateY(-10px);
            }
          }
        `}
      </style>
    </div>
  );
}

createRoot(document.body).render(<App />);

